import { Injectable } from '@angular/core';
import { ReportTypes, SubscriptionPlans } from './user/user.model';
import { MenuGroupType } from '../map/menu/menu-groups';
import {
  GOOGLE_ADS_ID,
  IntegrationsService,
} from '../shared/util/integrations';

/**
 * Second half of the `send_to` of the Google Ads conversion action
 * "Subscription purchase" (`AW-18370774239/<label>`), copied from that
 * action's event snippet in the Ads UI.
 *
 * Ads silently drops a hit whose `send_to` it does not recognise, so an
 * unset label must skip the conversion rather than fire something that only
 * looks like tracking - hence the empty-string branch below, which is what
 * this constant held before the action existed.
 */
export const SUBSCRIPTION_PURCHASE_CONVERSION_LABEL = '7ZULCOqU4NscEN-J77dE';

/** The addressed conversion action, or null while none is configured. */
export function subscriptionPurchaseSendTo(
  label: string = SUBSCRIPTION_PURCHASE_CONVERSION_LABEL
): string | null {
  return label ? `${GOOGLE_ADS_ID}/${label}` : null;
}

@Injectable({
  providedIn: 'root',
})
export class GoogleAnalyticsService {
  constructor(private integrations: IntegrationsService) {}

  public logChooseCoverageArea(
    plan: SubscriptionPlans,
    promoCode: string | null
  ) {
    this.integrations.track('choose_coverage_area', {
      subscription_plan: plan,
      subscription_promo_code: promoCode,
    });
  }

  public logSubscriptionGetStarted(
    plan: SubscriptionPlans,
    monthly: boolean,
    promoCode: string | null
  ) {
    this.integrations.track('SUBSCRIPTION_GET_STARTED', {
      subscription_plan: plan,
      subscription_period: monthly ? 'MONTHLY' : 'YEARLY',
      subscription_promo_code: promoCode,
    });
  }

  public logSubscriptionToPayment(
    plan: SubscriptionPlans,
    monthly: boolean,
    promoCode: string | null
  ) {
    this.integrations.track('SUBSCRIPTION_TO_PAYMENT', {
      subscription_plan: plan,
      subscription_period: monthly ? 'MONTHLY' : 'YEARLY',
      subscription_promo_code: promoCode,
    });
  }

  public logSeeAllIndicators(plan: SubscriptionPlans) {
    this.integrations.track('PROFILE_SUBSCRIPTION_ALL_INDICATORS', {
      subscription_plan: plan,
    });
  }

  /**
   * A sample-PDF click on /pricing's report cards. Deliberately not
   * `logSampleReportClicked` below: that event's only dimension is
   * `subscription_plan`, and feeding it a `ReportTypes` value would put
   * 'COMMUNITY_COMPASS' where every existing report and audience segment
   * expects a plan id.
   */
  public logReportSampleClicked(reportType: ReportTypes) {
    this.integrations.track('report_sample_clicked', {
      report_type: reportType,
    });
  }

  public logSampleReportClicked(plan: SubscriptionPlans) {
    this.integrations.track('PROFILE_SUBSCRIPTION_SAMPLE_REPORT', {
      subscription_plan: plan,
    });
  }

  public logToggleSubscriptionPeriod(monthly: boolean) {
    this.integrations.track('PROFILE_SUBSCRIPTION_PERIOD_TOGGLE', {
      subscription_period: monthly ? 'MONTHLY' : 'YEARLY',
    });
  }

  public logClicksReachedLimit() {
    this.integrations.track('FREE_CLICKS_LIMIT_REACHED', {});
  }

  public logReportOrderTypeConfirmed() {
    this.integrations.track('ORDER_REPORT_CONFIRM_TYPE', {});
  }

  public logReportOrderLocationConfirmed() {
    this.integrations.track('ORDER_REPORT_CONFIRM_LOCATION', {});
  }

  public logCompareLocationsClick(from: 'map' | 'compare-page') {
    this.integrations.track('COMPARE_CLICK', { compare_source: from });
  }

  public logMenuGroupChanged(group: MenuGroupType) {
    this.integrations.track('MENU_GROUP_CHANGED', { menu_group: group });
  }

  public logSignInGoogle() {
    this.integrations.track('sign_in_google', {});
  }

  public logSignInEmail() {
    this.integrations.track('sign_in_email', {});
  }

  public logLockedParamClickGuest() {
    this.integrations.track('locked_param_click_guest', {});
  }

  public logStartFreeTrialClick() {
    this.integrations.track('start_free_trial_click', {});
  }

  public logFormStart() {
    this.integrations.track('form_start', {});
  }

  public logCreateAccountClick() {
    this.integrations.track('create_account_click', {});
  }

  public logLockedParamClickRegistered() {
    this.integrations.track('locked_param_click_registered', {});
  }

  public logGetStartedLockedParam() {
    this.integrations.track('get_started_locked_param', {});
  }

  public logGetStartedPaywall(
    plan: SubscriptionPlans,
    monthly: boolean,
    promoCode: string | null
  ) {
    this.integrations.track('get_started_paywall', {
      subscription_plan: plan,
      subscription_period: monthly ? 'MONTHLY' : 'YEARLY',
      subscription_promo_code: promoCode,
    });
  }

  public logGetStartedProfile(
    plan: SubscriptionPlans,
    monthly: boolean,
    promoCode: string | null
  ) {
    this.integrations.track('get_started_profile', {
      subscription_plan: plan,
      subscription_period: monthly ? 'MONTHLY' : 'YEARLY',
      subscription_promo_code: promoCode,
    });
  }

  public logGuideUserCategory(category: string) {
    this.integrations.track('guide_user_category', { user_category: category });
  }

  public logUserChangedCategory(category: string) {
    this.integrations.track('user_changed_category', {
      user_category: category,
    });
  }

  public logSearchDone(source: 'suggestion' | 'history') {
    this.integrations.track('map_search_done', { search_source: source });
  }

  public logBeginCheckout(
    plan: SubscriptionPlans,
    monthly: boolean,
    promoCode: string | null
  ) {
    this.integrations.track('begin_checkout', {
      subscription_plan: plan,
      subscription_period: monthly ? 'MONTHLY' : 'YEARLY',
      subscription_promo_code: promoCode,
    });
  }

  // Sales Forecast fake-door funnel — these events are the go/no-go signal
  // for building the real forecast engine

  public logSalesForecastPageView(industry: string) {
    this.integrations.track('sales_forecast_page_view', {
      sales_forecast_industry: industry,
    });
  }

  public logSalesForecastAddressSelected(industry: string) {
    this.integrations.track('sales_forecast_address_selected', {
      sales_forecast_industry: industry,
    });
  }

  public logSalesForecastTeaserShown(industry: string) {
    this.integrations.track('sales_forecast_teaser_shown', {
      sales_forecast_industry: industry,
    });
  }

  // A visitor said "this page is not my business type" by taking one of the
  // hero chips. Both ends are sent: the page that failed them is the
  // interesting half (which industry page attracts the most mismatched
  // traffic), and the target says whether they fell back to the
  // configurable page or recognised one of the named presets.
  public logSalesForecastBusinessTypeSwitch(from: string, to: string) {
    this.integrations.track('business_type_switch', {
      sales_forecast_industry: from,
      sales_forecast_target_industry: to,
    });
  }

  public logSalesForecastComingSoonClick(industry: string) {
    this.integrations.track('sales_forecast_coming_soon_click', {
      sales_forecast_industry: industry,
    });
  }

  // Fired once per page visit, on the first edit of the customer profile
  // calculator — measures whether visitors engage with audience targeting
  public logSalesForecastProfileAdjusted(industry: string) {
    this.integrations.track('sales_forecast_profile_adjusted', {
      sales_forecast_industry: industry,
    });
  }

  // "Someone wants to convert" signal for a sales-forecast paywall/upgrade
  // prompt — the GA counterpart to SalesForecastFunnelService.
  // subscriptionClicked(), which logs the same click to our own backend.
  // Subscription-specific: do not fire this for a report-purchase click
  // (see logSalesForecastReportIntentClicked below) — mixing the two would
  // corrupt subscription-conversion analysis with unrelated clicks.
  public logSalesForecastSubscriptionClicked(placement: string) {
    this.integrations.track('sales_forecast_subscription_clicked', {
      sales_forecast_placement: placement,
    });
  }

  // An already-subscribed visitor clicked toward ordering the separate,
  // one-time paid report (e.g. the top-nav CTA) — a softer, earlier signal
  // than logSalesForecastCtaClicked below, which fires only once the real
  // Stripe checkout session is actually created in onCheckout(). Kept as
  // its own event rather than reusing either one: this is neither a
  // subscription action nor a confirmed checkout attempt.
  public logSalesForecastReportIntentClicked(placement: string) {
    this.integrations.track('sales_forecast_report_intent_clicked', {
      sales_forecast_placement: placement,
    });
  }

  public logSalesForecastCtaClicked(industry: string, priceUsd: number) {
    this.integrations.track('sales_forecast_cta_clicked', {
      sales_forecast_industry: industry,
      sales_forecast_price_usd: priceUsd,
    });
  }

  public logSalesForecastCheckoutCompleted(industry: string, priceUsd: number) {
    this.integrations.track('sales_forecast_checkout_completed', {
      sales_forecast_industry: industry,
      sales_forecast_price_usd: priceUsd,
    });
  }

  /**
   * An account was just created. GA4's standard event name, so it lands in
   * the built-in reports, and the one auth event on this page that is a
   * conversion rather than a click on the way to one.
   *
   * @param placement the paywall that opened the sign-up gate, so the
   *   sales-forecast funnel can tell a gated sign-up from a direct one.
   */
  public logSignUp(method: 'google' | 'email', placement: string) {
    this.integrations.track('sign_up', {
      method,
      sales_forecast_placement: placement,
    });
  }

  /** A returning user authenticated. Counted, never converted. */
  public logLogin(method: 'google' | 'email') {
    this.integrations.track('login', { method });
  }

  /**
   * The money event: a Stripe subscription checkout came back paid. Fired
   * from the success redirect (see SubscriptionPurchaseConversionService),
   * which is the first moment the purchase is real — every earlier event in
   * this file, begin_checkout included, only says someone intended to buy.
   *
   * Two hits on purpose. `conversion` is what Google Ads counts and
   * attributes back to the paid click that started the session, days
   * earlier, on a landing page. `purchase` is the GA4 counterpart, so the
   * funnel reads end to end inside GA4 as well.
   *
   * The value is the session's real total, read back from Stripe (see
   * SubscriptionsService.getCheckoutSessionAmount) rather than derived from
   * the plan: SubscriptionPlans.description holds display strings like
   * '$24', and a promo code entered on the Stripe page moves the number
   * after we have stopped looking. Null when that lookup failed, in which
   * case the conversion still fires and the Ads action's default value
   * applies — a purchase counted at the wrong value beats one not counted.
   *
   * @param transactionId the Stripe checkout session id, which deduplicates
   *   this conversion against a refresh of the success URL and against a
   *   later server-side upload of the same purchase.
   * @param value in major units (dollars), not Stripe's cents.
   */
  public logSubscriptionPurchase(
    plan: string,
    transactionId: string | null,
    value: number | null = null,
    currency: string | null = null
  ) {
    const money =
      value !== null && currency !== null
        ? { value, currency: currency.toUpperCase() }
        : {};

    const sendTo = subscriptionPurchaseSendTo();
    if (sendTo) {
      this.integrations.track('conversion', {
        send_to: sendTo,
        transaction_id: transactionId ?? undefined,
        ...money,
      });
    }

    this.integrations.track('purchase', {
      transaction_id: transactionId ?? undefined,
      subscription_plan: plan,
      items: [{ item_id: plan, item_name: plan }],
      ...money,
    });
  }
}
